import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiCheck, FiTrash2, FiCheckCircle, FiExternalLink } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import { useAuth } from "../../../services/useAuth";
import { hasPageAccessForUser } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import {
  buildAdaptiveSingleFilterSection,
  countTruthy,
} from "../shared/buildListFilterSections";
import Pagination from "../../../components/BaseUI/Pagination";
import {
  API_URL,
  SEVERITY_LOZENGE,
  getEventTypeLabel,
  getSeverityLabel,
  timeAgo,
  decodeNotificationText,
  getNotificationLink,
  getNotificationNavigation,
  PREFERENCE_EVENT_TYPES,
} from "./constants";

const tableColumns = [
  { name: "Başlık", dataType: "text", id: 0 },
  { name: "Mesaj", id: 1, sortable: false },
  { name: "Bildirim Türü", dataType: "text", id: 2 },
  { name: "Öncelik", dataType: "text", id: 3 },
  { name: "Okundu mu?", dataType: "text", id: 4 },
  { name: "Ne Zaman", dataType: "text", id: 5 },
  { name: "İşlemler", id: 6, sortable: false },
];

const NotificationsPage = () => {
  useTitle("Rovion MES - Bildirimler");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ isRead: "", severity: "", eventType: "" });

  const filterSections = useMemo(
    () => [
      buildAdaptiveSingleFilterSection({
        id: "isRead",
        title: "Okunma Durumu",
        value: filter.isRead,
        onChange: (v) => {
          setFilter((f) => ({ ...f, isRead: v }));
          setPage(1);
        },
        options: [
          { value: "", label: "Tüm bildirimler" },
          { value: "false", label: "Okunmamış" },
          { value: "true", label: "Okunmuş" },
        ],
        allValue: "",
      }),
      buildAdaptiveSingleFilterSection({
        id: "severity",
        title: "Öncelik",
        value: filter.severity,
        onChange: (v) => {
          setFilter((f) => ({ ...f, severity: v }));
          setPage(1);
        },
        options: [
          { value: "", label: "Tüm öncelikler" },
          { value: "info", label: "Bilgi" },
          { value: "warning", label: "Uyarı" },
          { value: "critical", label: "Kritik" },
        ],
        allValue: "",
      }),
      buildAdaptiveSingleFilterSection({
        id: "eventType",
        title: "Bildirim Türü",
        value: filter.eventType,
        onChange: (v) => {
          setFilter((f) => ({ ...f, eventType: v }));
          setPage(1);
        },
        options: [
          { value: "", label: "Tüm türler" },
          ...PREFERENCE_EVENT_TYPES.map(({ key, label }) => ({
            value: key,
            label,
          })),
        ],
        allValue: "",
      }),
    ],
    [filter.isRead, filter.severity, filter.eventType]
  );

  const activeFilterCount = useMemo(
    () => countTruthy([filter.isRead, filter.severity, filter.eventType]),
    [filter.isRead, filter.severity, filter.eventType]
  );
  const [toasts, setToasts] = useState([]);
  const [limit, setLimit] = useState(20);

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((p) => p.filter((t) => t.id !== id));
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (filter.isRead !== "") params.set("isRead", filter.isRead);
      if (filter.severity) params.set("severity", filter.severity);
      if (filter.eventType) params.set("eventType", filter.eventType);
      const { data } = await axios.get(
        `${API_URL}/api/notifications?${params}`,
        authHeaders(),
      );
      if (data.status) {
        setNotifications(data.notifications || []);
        setTotal(data.total || 0);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch {
      addToast("Bildirimler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filter, authHeaders, addToast]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, authHeaders());
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      addToast("Okundu işaretlenemedi", "danger");
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, authHeaders());
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
      addToast("Tüm bildirimler okundu", "success");
    } catch {
      addToast("İşlem başarısız", "danger");
    }
  };

  const deleteNotif = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${id}`, authHeaders());
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch {
      addToast("Silme başarısız", "danger");
    }
  };

  const openNotification = async (notification) => {
    const nav = getNotificationNavigation(notification);
    if (!nav) return;
    if (
      currentUser &&
      !hasPageAccessForUser(currentUser, nav.pathname)
    ) {
      addToast("Bu sayfaya erişim yetkiniz yok", "danger");
      return;
    }
    if (!notification.isRead) await markRead(notification._id);
    navigate(nav.pathname, nav.state ? { state: nav.state } : undefined);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <AppPage
      title="Bildirimler"
      description={
        unreadCount > 0
          ? `${unreadCount} okunmamış bildiriminiz var`
          : "Tüm bildirimlerinizi buradan görüntüleyebilirsiniz"
      }
      actions={
        unreadCount > 0 && (
          <Button appearance="primary-animated" icon={<FiCheckCircle />} onClick={markAllRead}>
            Tümünü Okundu İşaretle
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "danger" ? "danger" : "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <CardContainer>
        <Table 
          columns={tableColumns} 
          loading={loading} 
          search={false} 
          hideFooter={true}
          viewOptions={
            <ListViewOptionsPanel
              filterSections={filterSections}
              activeFilterCount={activeFilterCount}
              onApply={() => fetchNotifications()}
            />
          }
        >
          {notifications.length === 0 && !loading ? (
            <Tr>
              <Td colSpan={7}>
                <div className="text-center py-5 text-muted small">Henüz bildirim yok</div>
              </Td>
            </Tr>
          ) : (
            notifications.map((n) => {
              const hasLink = !!getNotificationLink(n);
              return (
              <Tr
                key={n._id}
                data={n}
                style={hasLink ? { cursor: "pointer" } : undefined}
                onClick={hasLink ? () => openNotification(n) : undefined}
              >
                <Td>
                  <strong style={{ fontSize: "0.875rem" }}>{decodeNotificationText(n.title)}</strong>
                </Td>
                <Td>
                  <span className="text-muted small">{decodeNotificationText(n.body)}</span>
                </Td>
                <Td>
                  <span className="small">{getEventTypeLabel(n.eventType)}</span>
                </Td>
                <Td>
                  <Lozenge appearance={SEVERITY_LOZENGE[n.severity] || "default-subtle"} isBold>
                    {getSeverityLabel(n.severity)}
                  </Lozenge>
                </Td>
                <Td>
                  {n.isRead ? (
                    <Lozenge appearance="default-subtle" isBold>Okundu</Lozenge>
                  ) : (
                    <Lozenge appearance="primary-subtle" isBold>Okunmadı</Lozenge>
                  )}
                </Td>
                <Td>
                  <span className="text-muted small">{timeAgo(n.createdAt)}</span>
                </Td>
                <Td>
                  <div className="d-flex gap-2">
                    {hasLink && (
                      <IconButton
                        icon={<FiExternalLink />}
                        label="İlgili kaydı aç"
                        appearance="subtle"
                        shape="circle"
                        onClick={(event) => {
                          event.stopPropagation();
                          openNotification(n);
                        }}
                      />
                    )}
                    {!n.isRead && (
                      <IconButton
                        icon={<FiCheck />}
                        label="Okundu"
                        appearance="subtle"
                        shape="circle"
                        onClick={(event) => {
                          event.stopPropagation();
                          markRead(n._id);
                        }}
                      />
                    )}
                    <IconButton
                      icon={<FiTrash2 />}
                      label="Sil"
                      appearance="subtle"
                      shape="circle"
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteNotif(n._id);
                      }}
                    />
                  </div>
                </Td>
              </Tr>
            );
            })
          )}
        </Table>

        {!loading && total > 0 && (
          <div className="mt-3 mb-2 px-3">
            <Pagination
              totalItems={total}
              totalPerPage={limit}
              currentPage={page}
              totalRecords={total}
              onPageChange={({ pagination: paginationData }) => {
                if (paginationData.itemsPerPage !== limit) {
                  setLimit(paginationData.itemsPerPage);
                  setPage(1);
                } else {
                  setPage(paginationData.currentPage);
                }
              }}
              options={{
                skipPrevious: true,
                previous: true,
                next: true,
                skipNext: true,
                selectQuantity: true,
                simpleCounting: false,
              }}
            />
          </div>
        )}
      </CardContainer>
    </AppPage>
  );
};

export default withAuth(NotificationsPage);
